export const POS_MAX_COORDINATE_METERS = 1_000_000;
export const POS_MAX_STRUCTURES = 500;

export function boundedNumber(value: unknown, minimum: number, maximum: number, fallback: number): number {
  const parsed = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.max(minimum, Math.min(maximum, parsed));
}

export function boundedInteger(value: unknown, minimum: number, maximum: number, fallback: number): number {
  return Math.floor(boundedNumber(value, minimum, maximum, fallback));
}

export function clampCoordinate(value: unknown): number {
  return boundedNumber(value, -POS_MAX_COORDINATE_METERS, POS_MAX_COORDINATE_METERS, 0);
}

export function normalizeSecurityStatus(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? Math.max(-1, Math.min(1, parsed)) : null;
}

export interface PosResourceValues {
  cpu?: number;
  powerGrid?: number;
}

export interface PosResourceHeadroom {
  cpu: number;
  powerGrid: number;
}

function validResource(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0;
}

/** Calculate the fitting resources left after currently-online structures. */
export function calculateRemainingPosResources(
  tower: PosResourceValues | null | undefined,
  structures: Array<PosResourceValues & { state: 'online' | 'offline' }>,
): PosResourceHeadroom | null {
  if (!tower || !validResource(tower.cpu) || !validResource(tower.powerGrid)) return null;
  const online = structures.filter(structure => structure.state === 'online');
  if (online.some(structure => !validResource(structure.cpu) || !validResource(structure.powerGrid))) return null;
  const used = online.reduce((total, structure) => ({
    cpu: total.cpu + structure.cpu!,
    powerGrid: total.powerGrid + structure.powerGrid!,
  }), { cpu: 0, powerGrid: 0 });
  return { cpu: tower.cpu - used.cpu, powerGrid: tower.powerGrid - used.powerGrid };
}

/** True only when one structure has known requirements and fits both resources. */
export function fitsRemainingPosResources(
  structure: PosResourceValues,
  available: PosResourceHeadroom | null | undefined,
): boolean {
  if (!available || !validResource(available.cpu) || !validResource(available.powerGrid)) return false;
  if (!validResource(structure.cpu) || !validResource(structure.powerGrid)) return false;
  return structure.cpu <= available.cpu && structure.powerGrid <= available.powerGrid;
}

export function radialPlacement(index: number, minimumDistance?: number, slots = 8): { x: number; y: number } {
  const safeIndex = Math.max(0, Math.floor(index));
  const safeSlots = Math.max(1, Math.floor(slots));
  const radius = Math.max(minimumDistance ?? 20_000, 20_000) + Math.floor(safeIndex / safeSlots) * 2_000;
  const angle = (safeIndex % safeSlots) * ((Math.PI * 2) / safeSlots);
  return { x: Math.round(Math.cos(angle) * radius), y: Math.round(Math.sin(angle) * radius) };
}

export function csvCell(value: unknown): string {
  return `"${String(value ?? '').replaceAll('"', '""')}"`;
}
